import * as React from 'react'
import { Popper } from '@mui/material'

type Place = 'below' | 'above' | 'left' | 'right' | 'row' | 'column'

interface IOwnProps {
  isOpen: boolean
  body: React.ReactNode
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
  place?: Place
  preferPlace?: Place
  offset?: number
}

const placementFor = (place?: Place): 'bottom-start' | 'top-start' =>
  place === 'above' ? 'top-start' : 'bottom-start'

// Anchors `children` inline and floats `body` beside it while `isOpen`, keeping the
// .Popover / .Popover-body / .Popover-<place> markup the app CSS targets.
// disablePortal keeps the body inside the anchor's DOM subtree so consumer-level
// react-click-outside wrappers still see menu clicks as inside.
const BasePopover: React.FunctionComponent<IOwnProps> = ({
  isOpen,
  body,
  children,
  className,
  style,
  place,
  preferPlace,
  offset = 0,
}) => {
  // Anchor to the trigger element itself (the span is display:contents so it adds no
  // box to the layout — the trigger stays a direct flex child, as before the migration).
  const [wrapper, setWrapper] = React.useState<HTMLElement | null>(null)
  const anchorEl = wrapper?.firstElementChild ?? wrapper
  const resolvedPlace = place ?? preferPlace ?? 'below'
  const placeClass =
    resolvedPlace === 'above' ? 'Popover-above' : 'Popover-below'

  return (
    <>
      <span
        ref={setWrapper}
        className="Popover-anchor"
        style={{ display: 'contents' }}
      >
        {children}
      </span>
      <Popper
        open={isOpen && Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement={placementFor(resolvedPlace)}
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [0, offset] } }]}
        style={style}
        className={['Popover', placeClass, className].filter(Boolean).join(' ')}
      >
        <div className="Popover-body">{body}</div>
      </Popper>
    </>
  )
}

export default BasePopover
